import { Suspense, lazy } from 'react'
import { Navigate, useRoutes } from 'react-router-dom'
import AuthLayout from '../layouts/AuthLayout'
import GuestGuard from '../guards/GuestGuard'
import AuthGuard from '../guards/AuthGuard'
import DashboardLayout from '../layouts/DashboardLayout'
import Dashboard from '../pages/Dashboard'
import VerifyUser from '../pages/auth/VerifyUser'
import PaymentLayout from '../layouts/PaymentLayout'
import TermsAndConditions from '../pages/TermsAndConditions'
import Profile from '../pages/auth/profile/Profile'
import StudentLicenses from '../pages/studentLicenses/StudentLicenses'
import CommercialLicenses from '../pages/studentLicenses/CommercialLicenses'
import LicenseGuard from '../guards/LicenseGuard'

const Loadable =
    <T extends React.ComponentType<any>>(Component: T) =>
    (props: React.ComponentProps<T>) => {
        return (
            <Suspense
                fallback={
                    <div>
                        {/* <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner> */}
                    </div>
                }
            >
                <Component {...props} />
            </Suspense>
        )
    }

const Router = () => {
    return useRoutes([
        {
            path: 'auth',
            element: <AuthLayout />,
            children: [
                {
                    path: 'sign-up',
                    element: (
                        <GuestGuard>
                            <SignUp />
                        </GuestGuard>
                    )
                },
                {
                    path: 'sign-in',
                    element: (
                        <GuestGuard>
                            <SignIn />
                        </GuestGuard>
                    )
                },
                {
                    path: 'verify-user',
                    element: (
                        <GuestGuard>
                            <VerifyUser />
                        </GuestGuard>
                    )
                },
                {
                    path: 'forgot-password',
                    element: (
                        <GuestGuard>
                            <ForgotPassword />
                        </GuestGuard>
                    )
                },
                {
                    path: 'forgot-username-or-email',
                    element: (
                        <GuestGuard>
                            <ForgotUsernameOrEmail />
                        </GuestGuard>
                    )
                },
                {
                    path: 'change-password',
                    element: (
                        <AuthGuard>
                            <ChangePassword />
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: '/',
                    element: (
                        <AuthGuard>
                            <Dashboard />
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '/payment',
            element: <PaymentLayout />,
            children: [
                {
                    path: ':orderId/:requestId',
                    element: <PaymentPage />
                },
                { path: '/payment', element: <Navigate to='/404' replace /> }
            ]
        },
        {
            path: '/',
            element: <PaymentLayout />,
            children: [
                {
                    path: 'terms-and-conditions',
                    element: <TermsAndConditions />
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: 'profile',
                    element: <Profile />
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: 'student-licenses',
                    element: (
                        <AuthGuard>
                            <LicenseGuard routeType='student'>
                                <StudentLicenses
                                    selectedLicenses={''}
                                    setSelectedLicenses={''}
                                    quantities={''}
                                    setQuantities={''}
                                    selectedLicensesDetails={''}
                                    setSelectedLicensesDetails={''}
                                    setModalOpen={''}
                                    isModalOpen={''}
                                    setMainTab={''}
                                />
                            </LicenseGuard>
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: 'commercial-licenses',
                    element: (
                        <AuthGuard>
                            <LicenseGuard routeType='commercial'>
                                <CommercialLicenses
                                    selectedLicenses={''}
                                    setSelectedLicenses={''}
                                    quantities={''}
                                    setQuantities={''}
                                    selectedLicensesDetails={''}
                                    setSelectedLicensesDetails={''}
                                    setModalOpen={''}
                                    isModalOpen={''}
                                    setMainTab={''}
                                />
                            </LicenseGuard>
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: 'training-courses',
                    element: (
                        <AuthGuard>
                            <Dashboard />
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '/',
            element: <DashboardLayout />,
            children: [
                {
                    path: 'evaluation-licenses',
                    element: (
                        <AuthGuard>
                            <Dashboard />
                        </AuthGuard>
                    )
                }
            ]
        },
        {
            path: '*',
            children: [
                { path: '404', element: <Page404 /> },
                { path: '*', element: <Navigate to='/404' replace /> }
            ]
        }
    ])
}

// Import components

const SignUp = Loadable(lazy(() => import('../pages/auth/SignUp')))
const SignIn = Loadable(lazy(() => import('../pages/auth/SignIn')))
const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')))
const PaymentPage = Loadable(lazy(() => import('../pages/payment/PaymentPage')))
const ForgotUsernameOrEmail = Loadable(lazy(() => import('../pages/auth/ForgotUsernameOrEmail')))
const ChangePassword = Loadable(lazy(() => import('../pages/auth/ChangePassword')))
const Page404 = Loadable(lazy(() => import('../pages/pages404')))

export default Router
